import { Observable, fromEventPattern } from 'rxjs';
import { Injectable, Signal } from '@angular/core';
import { Auth, AuthErrorCodes, getAuth, GoogleAuthProvider, indexedDBLocalPersistence, signInWithPopup, User, UserCredential } from 'firebase/auth';
import { FirebaseApp, initializeApp } from 'firebase/app';
import { AnalyticsService } from '../analytics/analytics.service';
import { environment } from 'src/environments/environment';
import { AuthError } from './auth-error';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly firebaseConfig = environment.firebaseConfig;
  private readonly app: FirebaseApp;
  private readonly auth: Auth;

  // Public observables for auth state
  readonly user$: Observable<User | null | undefined>;
  readonly idToken$: Observable<string | null | undefined>;
  user: Signal<User | null | undefined>;
  idToken: Signal<string | null | undefined>;

  // Auth state
  private _isLoggingIn = false;
  get isLoggingIn(): boolean {
    return this._isLoggingIn;
  }

  private tokenRefreshInterval: any;

  constructor(private readonly analyticsService: AnalyticsService) {
    this.app = initializeApp(this.firebaseConfig);
    this.auth = getAuth(this.app);

    // Initialize auth settings
    this.initializeAuth();

    this.user = toSignal(this.createAuthStateObservable());
    this.idToken = toSignal(this.createIdTokenObservable());

    // Setup auth state observables
    this.user$ = toObservable(this.user);
    this.idToken$ = toObservable(this.idToken);
  }

  private initializeAuth(): void {
    this.auth.useDeviceLanguage();
    this.auth.setPersistence(indexedDBLocalPersistence).catch((error) => {
      console.error('Error setting auth persistence:', error);
    });
  }

  private createAuthStateObservable(): Observable<User | null | undefined> {
    return new Observable<User | null>((subscriber) => {
      const unsubscribe = this.auth.onAuthStateChanged(subscriber);
      return () => unsubscribe();
    });
  }

  private createIdTokenObservable(): Observable<string | null | undefined> {
    return fromEventPattern<string | null>(
      (handler) =>
        this.auth.onIdTokenChanged(async (user) => {
          if (!user) {
            // Clear interval if user is null
            if (this.tokenRefreshInterval) {
              clearInterval(this.tokenRefreshInterval);
              this.tokenRefreshInterval = null;
            }
            handler(null);
            return;
          }
          // Set interval to refresh token
          if (!this.tokenRefreshInterval) {
            this.tokenRefreshInterval = setInterval(
              async () => {
                try {
                  await user.getIdToken(true);
                } catch (error) {
                  console.error('Error refreshing ID token:', error);
                }
              },
              30 * 60 * 1000
            ); // 30 minutes
          }
          try {
            const token = await user.getIdToken();
            handler(token);
          } catch (error) {
            console.error('Error getting ID token:', error);
            handler(null);
          }
        }),
      () => undefined // Cleanup handled by Firebase
    );
  }

  private createGoogleProvider(swapAccount: boolean = false): GoogleAuthProvider {
    const provider = new GoogleAuthProvider();
    provider.addScope('profile');
    provider.addScope('email');

    if (swapAccount) {
      provider.setCustomParameters({ prompt: 'select_account' });
    }

    return provider;
  }

  private handleAuthError(error: any, context: string): never {
    console.error(`Error in ${context}:`, error);
    throw new AuthError(`Failed to ${context}`, error.code);
  }

  async googleSignIn(swapAccount: boolean = true): Promise<UserCredential> {
    this._isLoggingIn = true;

    try {
      const provider = this.createGoogleProvider(swapAccount);
      const credential = await signInWithPopup(this.auth, provider);

      // Consider user as new if the account was created within the last 10 seconds (10000 milliseconds)
      const creationTime = credential.user.metadata.creationTime;
      if (creationTime && new Date().getTime() - new Date(creationTime).getTime() < 10000) {
        this.analyticsService.sendSignUpEvent('Google');
      }

      return credential;
    } catch (error: any) {
      if (error.code === AuthErrorCodes.POPUP_CLOSED_BY_USER) {
        throw new AuthError('Authentication cancelled by user', error.code);
      }
      this.handleAuthError(error, 'sign in with Google');
    } finally {
      this._isLoggingIn = false;
    }
  }

  async deleteAccount(): Promise<boolean> {
    const user = this.auth.currentUser;

    if (!user) {
      throw new AuthError('No user is currently signed in');
    }

    try {
      await user.delete();
      this.analyticsService.sendAccountDeletedEvent();
      return true;
    } catch (error: any) {
      console.error('Error deleting account:', error);
      throw new AuthError('Failed to delete account', error.code);
    }
  }

  async signOut(): Promise<void> {
    try {
      await this.auth.signOut();
    } catch (error: any) {
      throw new AuthError('Failed to sign out', error.code);
    }
  }
}
